export const PLAN_NAMES = { trial: "Trial", monthly: "Mensal", annual: "Anual", pro_monthly: "Mensal (legado)", pro_annual: "Anual (legado)", expired: "Expirado", lifetime: "Vitalício", banned: "Banido", deleted: "Encerrado", pending: "Sem plano" };
export const PAID_PLANS = ["monthly", "annual", "pro_monthly", "pro_annual"];
export const PLATFORM_NAMES = { android: "Android", ios: "iOS", windows: "Windows", web: "Web" };
export const COLLECTION_NAMES = { filamentos: "Filamentos", produtos: "Produtos", pedidos: "Pedidos", vendas: "Vendas", clientes: "Clientes", impressoras: "Impressoras", orcamentos: "Orçamentos", historico_calculos: "Cálculos", agenda: "Agenda", caixa: "Caixa", fila_impressao: "Fila", pedidos_online: "Loja" };
export const DAY = 86400000;
export const timestamp = value => value == null || value === "" ? NaN : new Date(value).getTime();
export const money = value => Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
export const date = value => Number.isFinite(timestamp(value)) ? new Date(value).toLocaleDateString("pt-BR") : "—";
export const dateTime = value => Number.isFinite(timestamp(value)) ? new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "Sem registro";
export const normalize = value => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
export const matches = (query, ...fields) => normalize(fields.join(" ")).includes(normalize(query));
export const expiry = user => user.plan === "lifetime" ? null : user.plan === "trial" ? user.trialEndsAt : user.planExpiresAt;
export const hoursSince = (value, now = Date.now()) => Number.isFinite(timestamp(value)) ? Math.max(0, (now - timestamp(value)) / 3600000) : Infinity;
export function relative(value, now = Date.now()) {
  const hours = hoursSince(value, now);
  if (!Number.isFinite(hours)) return "Sem registro";
  if (hours < 1 / 12) return "Agora";
  if (hours < 1) return `Há ${Math.floor(hours * 60)} min`;
  if (hours < 24) return `Há ${Math.floor(hours)} h`;
  const days = Math.floor(hours / 24);
  return `Há ${days} dia${days === 1 ? "" : "s"}`;
}
export function activeSubscription(user, now = Date.now()) {
  return user.cadastroCompleto !== false && !user.semConta && PAID_PLANS.includes(user.plan) && timestamp(user.planExpiresAt) > now;
}
export function filterUsers(users, { search = "", plan = "todos", usage = "todos", order = "recentes" } = {}, now = Date.now()) {
  return users.filter(user => {
    // A busca também acha pelo celular (com ou sem máscara: só os dígitos)
    const digitos = search.replace(/\D/g, "");
    if (!matches(search, user.nome, user.empresa, user.email, user.telefone) && !(digitos.length >= 4 && String(user.telefone || "").includes(digitos))) return false;
    if (plan === "incompleto" ? user.cadastroCompleto !== false : plan === "pending" ? !["pending", "—", "", null, undefined].includes(user.plan) : plan !== "todos" && user.plan !== plan) return false;
    const hours = hoursSince(user.ultimoUso, now);
    if (usage === "24h" && hours > 24) return false;
    if (usage === "7d" && hours > 168) return false;
    if (usage === "30d" && hours <= 720) return false;
    return true;
  }).sort((a, b) => order === "nome" ? (a.nome || a.email || "").localeCompare(b.nome || b.email || "", "pt-BR")
    : order === "vencimento" ? (timestamp(expiry(a)) || Infinity) - (timestamp(expiry(b)) || Infinity)
    : order === "cadastro" ? (timestamp(b.criadaEm) || 0) - (timestamp(a.criadaEm) || 0)
    : (timestamp(b.ultimoUso) || 0) - (timestamp(a.ultimoUso) || 0));
}
export function userMetrics(users, prices, now = Date.now()) {
  const active = users.filter(u => activeSubscription(u, now));
  const within = (value, days) => timestamp(value) > now && timestamp(value) <= now + days * DAY;
  const validPrices = Number.isFinite(Number(prices?.precoMensal)) && Number(prices?.precoMensal) > 0 && Number.isFinite(Number(prices?.precoAnual)) && Number(prices?.precoAnual) > 0;
  return {
    active, total: users.length,
    recent: users.filter(u => hoursSince(u.ultimoUso, now) <= 24).length,
    weekly: users.filter(u => hoursSince(u.ultimoUso, now) <= 168).length,
    newUsers: users.filter(u => timestamp(u.criadaEm) <= now && timestamp(u.criadaEm) >= now - 7 * DAY).length,
    incomplete: users.filter(u => u.cadastroCompleto === false).length,
    trial: users.filter(u => u.plan === "trial" && timestamp(u.trialEndsAt) > now).length,
    trialEnding: users.filter(u => u.plan === "trial" && within(u.trialEndsAt, 3)),
    expiring: active.filter(u => within(u.planExpiresAt, 7)),
    inactive: active.filter(u => hoursSince(u.ultimoUso, now) > 168),
    noRenewal: active.filter(u => u.autoRenewCancelled).length,
    missingExpiry: users.filter(u => PAID_PLANS.includes(u.plan) && !Number.isFinite(timestamp(u.planExpiresAt))).length,
    estimate: validPrices ? active.reduce((sum, u) => sum + Number(u.plan.includes("annual") ? prices.precoAnual / 12 : prices.precoMensal), 0) : null,
  };
}
export function couponState(coupon) {
  if (!coupon.active) return "Inativo";
  if (Number(coupon.maxUses) > 0 && Number(coupon.usedCount || 0) >= Number(coupon.maxUses)) return "Esgotado";
  return "Disponível";
}
export function sumKnown(values) {
  const known = values.filter(value => typeof value === "number" && Number.isFinite(value));
  if (!known.length && values.length) return "—";
  const total = known.reduce((sum, value) => sum + value, 0);
  return known.length === values.length ? total : `${total} (parcial)`;
}
export function validateCoupon({ code, discount, type, plan, maxUses }) {
  if (!/^[A-Z0-9_-]{3,40}$/.test(String(code || "").trim().toUpperCase())) return "Use de 3 a 40 letras, números, hífen ou sublinhado no código.";
  if (!["fixed", "percent"].includes(type) || !["both", "monthly", "annual"].includes(plan)) return "Selecione o tipo de desconto e o plano.";
  if (!Number.isFinite(Number(discount)) || Number(discount) <= 0 || (type === "percent" && Number(discount) > 100)) return "Informe um desconto maior que zero (até 100% para percentual).";
  if (!Number.isInteger(Number(maxUses)) || Number(maxUses) < 0) return "O limite de usos deve ser um número inteiro maior ou igual a zero.";
  return "";
}
